import React from 'react';
import * as yup from 'yup';
import { useForm, SubmitHandler } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  AnalysisTypeField,
  CarrierListField,
  CarrierTiersField,
  EnterpriseSearchField,
  FileNameField,
  ImpactScacRestrictionsField,
  MarkupAmountField,
  MarkupPercentageTypeField,
  MarkupTypeField,
  NotifyEmailListField,
  PullDataDateRangeField,
  RateTypeField,
  RequestTypesField,
} from '../fields';
import {
  ANALYSIS_TYPES,
  BG_BLANKET_ACCOUNT,
  FORM_TITLES,
  MARKUP_TYPES,
  RATE_TYPES,
  REQUEST_TYPES,
} from '../constants';
import { ZipCodeField, StatesField, SubmissionButton } from '../../components';
import { validators } from '../utilities';
import { useAuth } from '../../auth';
import { pullDataRequestColumnBuilders } from './workbookColumnBuilders';
import { CONTAINERS } from '../../../../shared-jarvis';
import { analysisFieldSchema } from '../analysisFieldSchema';
import {
  withUploadToBlobStorage,
  WithUploadToBlobStorageProps,
} from '../withUploadToBlobStorage';

const schema = yup.object({
  dateRange: validators.dateRange,
  ...analysisFieldSchema,
});

export type PullDataAnalyzeFormFields = yup.InferType<typeof schema>;

const Form = ({
  handleFormSubmit,
  submitting,
}: WithUploadToBlobStorageProps<PullDataAnalyzeFormFields>) => {
  const auth = useAuth();

  const {
    control,
    formState: { errors },
    handleSubmit,
    resetField,
    setValue,
    watch,
  } = useForm<PullDataAnalyzeFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      carrierTiers: [],
      carrierExclusions: [],
      carrierInclusions: [],
      originStateExclusions: [],
      destinationStateExclusions: [],
      accounts: [],
      notifyEmails: [{ value: auth.user?.email }],
      dateRange: {
        startDate: null,
        endDate: null,
      },
    },
    shouldUnregister: true,
  });

  const selectedRequestType = watch('requestType');
  const selectedRateType = watch('rateType');
  const selectedMarkupType = watch('markupType');
  const selectedAnalysisType = watch('analysisType');
  const selectedAccounts = watch('accounts');
  const selectedCarrierTiers = watch('carrierTiers');

  // Request Type selection side-effects
  React.useEffect(() => {
    if (selectedRequestType === REQUEST_TYPES.BLANKETS) {
      setValue('rateType', RATE_TYPES.CARRIER);
      setValue('accounts', [BG_BLANKET_ACCOUNT]);
      resetField('markupType');
    }

    if (selectedRequestType === REQUEST_TYPES.ASP) {
      setValue('markupType', MARKUP_TYPES.CURRENT);
      setValue(
        'accounts',
        selectedAccounts.filter(
          (selectedAccount) =>
            selectedAccount?.accountNumber !== BG_BLANKET_ACCOUNT.accountNumber
        )
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedAccounts intentionally omitted: effect should only run on requestType change, not on every account selection
  }, [selectedRequestType, setValue, resetField]);

  const onSubmit: SubmitHandler<PullDataAnalyzeFormFields> = async (
    formData
  ) => {
    handleFormSubmit(
      formData,
      CONTAINERS.RATEQUALITY_SHIPMENTS_RETRIEVAL,
      pullDataRequestColumnBuilders
    );
  };

  return (
    <>
      <Typography
        align="center"
        component="h1"
        variant="h4"
        sx={{ mb: 3, mt: 2 }}
      >
        {FORM_TITLES.PULL_DATA_ANALYSIS_REQUEST}
      </Typography>

      <form
        style={{ marginTop: '1rem', marginBottom: 0 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FileNameField<PullDataAnalyzeFormFields>
          control={control}
          name="fileName"
          error={errors.fileName}
        />

        <PullDataDateRangeField<PullDataAnalyzeFormFields>
          control={control}
          name="dateRange"
          error={errors.dateRange}
        />

        <RequestTypesField<PullDataAnalyzeFormFields>
          control={control}
          name="requestType"
          error={errors.requestType}
        />

        <RateTypeField<PullDataAnalyzeFormFields>
          disabled={selectedRequestType === REQUEST_TYPES.BLANKETS}
          control={control}
          name="rateType"
          error={errors.rateType}
        />

        <EnterpriseSearchField<PullDataAnalyzeFormFields>
          control={control}
          error={errors.accounts as any}
          helperText="Search by Account Name or Number"
          label="Account"
          name="accounts"
          user={auth.user!}
        />

        <CarrierTiersField<PullDataAnalyzeFormFields>
          control={control}
          name="carrierTiers"
          error={errors.carrierTiers}
        />

        <CarrierListField<PullDataAnalyzeFormFields>
          control={control}
          error={errors.carrierExclusions}
          filterOptions={(options) =>
            options.filter((opt) =>
              selectedCarrierTiers?.includes(opt.carrier.tier)
            )
          }
          label="Exclude Carriers"
          name="carrierExclusions"
        />

        <CarrierListField<PullDataAnalyzeFormFields>
          control={control}
          error={errors.carrierExclusions}
          filterOptions={(options) =>
            options.filter(
              (opt) => !selectedCarrierTiers?.includes(opt.carrier.tier)
            )
          }
          label="Include Carriers"
          name="carrierInclusions"
        />

        <AnalysisTypeField<PullDataAnalyzeFormFields>
          control={control}
          name="analysisType"
          error={errors.analysisType}
        />

        <ImpactScacRestrictionsField<PullDataAnalyzeFormFields>
          control={control}
          error={errors.impactScacRestrictions}
          hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
          name="impactScacRestrictions"
        />

        <ZipCodeField<PullDataAnalyzeFormFields>
          control={control}
          error={errors.originZipExclusions}
          hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
          label="Origin Zip Exclusions"
          name="originZipExclusions"
        />

        <StatesField<PullDataAnalyzeFormFields>
          control={control}
          name="originStateExclusions"
          label="Origin State/Province Exclusions"
          hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
        />

        <ZipCodeField<PullDataAnalyzeFormFields>
          control={control}
          error={errors.destinationZipExclusions}
          hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
          label="Destination Zip Exclusions"
          name="destinationZipExclusions"
        />

        <StatesField<PullDataAnalyzeFormFields>
          control={control}
          name="destinationStateExclusions"
          label="Destination State/Province Exclusions"
          hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
        />

        <MarkupTypeField<PullDataAnalyzeFormFields>
          control={control}
          error={errors.markupType}
          filterOptions={(options) => {
            if (selectedRequestType === REQUEST_TYPES.BLANKETS) {
              return options.filter((o) => o.value !== MARKUP_TYPES.CURRENT);
            }

            return options;
          }}
          hidden={selectedRateType !== RATE_TYPES.CARRIER}
          name="markupType"
        />

        <MarkupPercentageTypeField<PullDataAnalyzeFormFields>
          control={control}
          hidden={selectedMarkupType !== MARKUP_TYPES.PERCENT}
          name="markupPercentType"
          error={errors.markupPercentType}
        />

        <MarkupAmountField<PullDataAnalyzeFormFields>
          control={control}
          name="markupAmount"
          error={errors.markupAmount}
          amountType={
            selectedMarkupType === MARKUP_TYPES.PERCENT
              ? 'Percentage'
              : 'Dollar'
          }
          hidden={
            selectedRateType !== RATE_TYPES.CARRIER ||
            // selectedMarkupType === MARKUP_TYPES.SEGMENT ||
            selectedMarkupType === MARKUP_TYPES.CURRENT
          }
        />

        <NotifyEmailListField<PullDataAnalyzeFormFields>
          control={control}
          error={errors.notifyEmails}
          name="notifyEmails"
          hidden={!auth.isBGEmployee}
        />

        <SubmissionButton
          loading={submitting}
          buttonProps={{
            sx: { mt: 3, p: 2 },
            fullWidth: true,
            type: 'submit',
          }}
        />
      </form>
    </>
  );
};

export const PullDataAnalyzeForm = withUploadToBlobStorage(Form);
